import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Page from '../../../components/page/Page';
import { Box, Button, ButtonBase, Hidden, LinearProgress, Paper, Typography } from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BrandLoader from '../../../components/BrandLoader';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCourseCore } from '../../../../core/modules/course';
import { Class } from '../../../../core/interfaces/class.interface';
import useThemeMediaQuery from '../../../../core/hooks/useThemeMediaQuery';
import ClassInfo from '../../../components/meCourse/ClassInfo';
import RatingInfo from '../../../components/meCourse/RatingInfo';
import mixpanel from 'mixpanel-browser';
import StepsModal from '../../../components/meCourse/StepsModal';

const MeCourse = () => {
  const { id } = useParams();
  const {
    course,
    modules,
    modulesStatus,
    getCourse,
    getModules,
    getClassComments,
    getViews,
    getRating,
    setCurrentClassId,
    expandModule,
    viewed,
  } = useCourseCore();
  
  const location = useLocation();
  const navigate = useNavigate();
  const pageLayout = useRef(null);
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));

  const [leftSidebarOpen, setLeftSidebarOpen] = useState(!isMobile);
  const [currentClass, setCurrentClass] = useState(1);
  const [rating, setRating] = useState(false);

  const getQueryParam = (name: string) => {
    return new URLSearchParams(location.search).get(name);
  };

  const classId = getQueryParam('classId');

  const loadCourseData = useCallback(() => {
    if (!id) {
      return;
    }

    getCourse(id, ({ course }) => {
      if (process.env.REACT_APP_MIXPANEL_TOKEN) {
        mixpanel.track('Viewing course', {
          'id': course.id,
          'name': course.name,
        });
      }
    });
    getModules(id);
    getViews(id);
    getRating(id);
  }, [getCourse, getModules, getRating, getViews, id]);

  useEffect(() => {
    loadCourseData();
  }, [loadCourseData]);

  const classes = useMemo(() => {
    return modules.reduce((prev: Class[], curr) =>
      [...prev, ...curr.classes.map((mClass) => ({ ...mClass.class, moduleId: mClass.moduleId, createdAt: mClass.createdAt }))]
    , []);
  }, [modules]);

  useEffect(() => {
    if (classId) {
      const classIdx = classes.findIndex((clazz) => `${clazz.id}` === `${classId}`);

      if (classIdx >= 0) {
        setCurrentClass(classIdx + 1);
        setCurrentClassId(classId);
        expandModule(classes[classIdx].moduleId);
      }
    }
  }, [classId, classes, expandModule, setCurrentClassId]);

  useEffect(() => {
    if (course) {
      if (classId) {
        getClassComments(course.id, classId);
      } else if (classes[0]) {
        getClassComments(course.id, classes[0].id);
      }
    }
  }, [classId, classes, course, getClassComments]);

  const updateCurrentStep = useCallback((index: number) => {
    setRating(false);

    if (index > classes.length || index < 1) {
      return;
    }

    // La clase elegida queda en la dirección (?classId=…) para que el link copiado abra esa misma clase.
    // Los efectos de arriba la leen y cargan sus comentarios.
    const params = new URLSearchParams(location.search);
    params.set('classId', `${classes[index - 1].id}`);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
    setCurrentClass(index);
  }, [classes, location.pathname, location.search, navigate]);

  const handleStepChange = useCallback((mId: string, cId: string) => {
    const mIndex = modules.findIndex((module) => module.id === mId); 

    const classesCount = modules
      .slice(0, mIndex)
      .reduce((prev, curr) => prev + curr.classes.length, 0);

    const cIndex = modules[mIndex].classes.findIndex((clazz) => clazz.class.id === cId);
    updateCurrentStep(classesCount + cIndex + 1);
    // En el celular el índice ocupa toda la pantalla: al elegir una clase se cierra y se ve la clase
    if (isMobile) {
      setLeftSidebarOpen(false);
      window.scrollTo({ top: 0 });
    }
  }, [isMobile, modules, updateCurrentStep]);

  const goToNextClass = useCallback(() => {
    const nextClass = classes[currentClass];
    if (!nextClass) {
      return;
    }

    if (nextClass.moduleId) {
      expandModule(nextClass.moduleId);
    }
    updateCurrentStep(currentClass + 1);
  }, [classes, currentClass, expandModule, updateCurrentStep]);

  const toggleRating = useCallback(() => {
    setRating(!rating);
  }, [rating]);

  // Resumen para el botón "Contenido del curso" del celular
  const contentSummary = useMemo(() => {
    const current = classes[currentClass - 1];
    const moduleIndex = current ? modules.findIndex((module) => module.id === current.moduleId) : -1;
    const module = moduleIndex >= 0 ? modules[moduleIndex] : undefined;
    const classIndex = module && current ? module.classes.findIndex((mClass) => mClass.class.id === current.id) : -1;
    const completed = classes.filter((clazz) => viewed.includes(clazz.id)).length;
    return { moduleIndex, module, classIndex, completed, total: classes.length };
  }, [classes, currentClass, modules, viewed]);

  const openContent = useCallback(() => {
    const current = classes[currentClass - 1];
    if (current?.moduleId) {
      expandModule(current.moduleId);
    }
    setLeftSidebarOpen(true);
  }, [classes, currentClass, expandModule]);

  const isLoadingModules = modulesStatus === 'idle' || modulesStatus === 'loading';
  const hasModulesError = modulesStatus === 'error';

  const mainContent = (() => {
    if (isLoadingModules) {
      return <BrandLoader message="Cargando contenido del curso..." />;
    }

    if (hasModulesError) {
      return (
        <Box className="flex flex-col items-center justify-center w-full py-96 gap-16 px-24 text-center">
          <Typography className="text-18 font-semibold text-gray-800">
            No pudimos cargar el curso
          </Typography>
          <Typography className="text-14 text-gray-500 max-w-md">
            Hubo un problema al obtener las clases. Reintentá; si sigue fallando, cerrá sesión y volvé a entrar.
          </Typography>
          <Button variant="contained" color="secondary" onClick={loadCourseData}>
            Reintentar
          </Button>
        </Box>
      );
    }

    if (rating) {
      return <RatingInfo />;
    }

    return <ClassInfo classes={classes} currentClass={currentClass} onNext={goToNextClass} />;
  })();

  return (
    <Page
      content={
        <div className="w-full">
          <Hidden lgUp>
            <Paper
              className="flex sticky top-0 z-10 items-center w-full px-12 py-10 border-b-1"
              square
              sx={{
                borderColor: 'rgba(148,163,184,0.22)',
                backgroundColor: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(14px)',
              }}
            >
              <ButtonBase
                onClick={openContent}
                aria-label="Contenido del curso"
                className="w-full text-left"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.75,
                  py: 1.25,
                  borderRadius: 3,
                  border: '1px solid #E2E8F0',
                  backgroundColor: '#fff',
                }}
              >
                <FormatListBulletedIcon sx={{ color: 'secondary.main' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#0F172A', lineHeight: 1.3 }}>
                    Contenido del curso
                  </Typography>
                  {contentSummary.module ? (
                    <Typography noWrap sx={{ fontSize: 12.5, color: '#64748B' }}>
                      Módulo {contentSummary.moduleIndex + 1} de {modules.length} · {contentSummary.module.name} · clase {contentSummary.classIndex + 1} de {contentSummary.module.classes.length}
                    </Typography>
                  ) : null}
                  <LinearProgress
                    variant="determinate"
                    value={contentSummary.total ? (contentSummary.completed * 100) / contentSummary.total : 0}
                    sx={{ mt: 0.75, height: 4, borderRadius: 2, backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: 'secondary.main', borderRadius: 2 } }}
                  />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 12.5, color: 'secondary.main', whiteSpace: 'nowrap' }}>
                  {contentSummary.completed}/{contentSummary.total}
                </Typography>
                <ExpandMoreIcon sx={{ color: '#64748B' }} />
              </ButtonBase>
            </Paper>
          </Hidden>

          {mainContent}
        </div>
      }
      drawerVariant='persistent'
      leftSidebarOpen={leftSidebarOpen}
      leftSidebarOnClose={() => {
        setLeftSidebarOpen(false);
      }}
      leftSidebarWidth={isMobile ? window.innerWidth : 300}
      leftSidebarContent={
        <StepsModal
          selectedClassId={classes[currentClass - 1]?.id}
          modules={modules}
          onChanging={handleStepChange}
          toggleRating={toggleRating}
          onClose={isMobile ? () => setLeftSidebarOpen(false) : undefined}
        />
      }
      scroll={isMobile ? 'normal' : 'content'}
      ref={pageLayout}
    />
  );
};

export default MeCourse;
